import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlarmClock,
  CheckCircle,
  ChevronDown,
  Coffee,
  LogIn,
  LogOut,
  Pause,
  UtensilsCrossed,
} from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { useGetMyFineSummary } from '../../hooks/use-attendance';
import {
  ClockStatusBadge,
  formatClockTime,
  formatElapsed,
  useAttendanceClock,
} from '../../hooks/use-attendance-clock';

/** A time tile on the clock card (Component Map › Timer card: muted tiles). */
const ClockTile = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) => (
  <div className="grid min-w-0 gap-0.5 rounded-md border bg-muted px-3 py-2 max-md:px-2 max-md:py-1.5">
    <span className="break-words text-xs text-muted-foreground">{label}</span>
    {loading ? (
      <span className="mt-1 block h-6 w-16 animate-pulse rounded bg-background" />
    ) : (
      <b className="truncate text-xl font-semibold leading-7 tabular-nums max-md:text-[15px] max-md:leading-[22px]">
        {value}
      </b>
    )}
  </div>
);

/** Fines are summed from float amounts (attendance fines are computed), so show 2 decimals. No
 * currency: neither the fine rule (catalog `CORRECTION_FINE.amount`) nor the Fine records name one. */
const formatFine = (amount: number) => amount.toFixed(2);

/** One part of the year's fine, as a bordered chip (Component Map › Fines footer). */
const FineStat = ({ label, amount }: { label: string; amount: number }) => (
  <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-[13px] text-muted-foreground max-md:justify-between">
    {label}
    <b className="font-semibold tabular-nums text-foreground">{formatFine(amount)}</b>
  </span>
);

/** The card's action buttons share the row; one alone takes half of it (prototype `.att-btns`). */
const ACTION_BUTTON = 'flex-auto max-md:basis-[calc(50%-4px)]';

/** The Attendance timer card; its state and actions come from `useAttendanceClock`. */
export const ClockWidget = () => {
  const { t } = useTranslation();
  const [isFineExpanded, setIsFineExpanded] = useState(false);
  const clock = useAttendanceClock();
  const { attendance, isLoading, phase, show, busy, actions } = clock;
  const { data: fineSummary, isLoading: isFineLoading } = useGetMyFineSummary();

  const fineTotal = fineSummary && (
    <span className="flex items-baseline gap-2 text-sm">
      <span className="text-muted-foreground">{t('CURRENT_HR_YEAR_FINE')}</span>
      <b className="text-base font-semibold tabular-nums">{formatFine(fineSummary.grandTotal)}</b>
    </span>
  );

  return (
    <>
      {/* Component Map › Timer card: a card (its own tokens, no dark hero). Left: the date, the live
          status and the worked-time timer; right: the time tiles, the actions and the rule messages;
          one column below 1200px of window (about 880px of card). The fines run along the bottom. */}
      <section
        aria-label={t('WORKED_TODAY')}
        className="rounded-lg border bg-card px-6 py-5 text-card-foreground shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] max-md:p-4"
      >
        <div className="grid grid-cols-1 items-start gap-4 min-[1200px]:grid-cols-[4fr_3fr] min-[1200px]:gap-6">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 max-md:justify-between">
              <span className="min-w-0 break-words text-lg font-medium leading-7 max-md:text-base">
                {new Date().toLocaleDateString(undefined, {
                  timeZone: getOrganizationTimeZone(),
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              <ClockStatusBadge phase={phase} isOnLunchBreak={clock.isOnLunchBreak} />
            </div>
            <div className="grid gap-1 max-md:justify-items-center max-md:text-center">
              <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
                {phase === 'break' && <Pause className="size-3.5" />}
                {phase === 'done' ? t('TOTAL_WORKED') : t('WORKED_TODAY')}
              </span>
              {isLoading ? (
                <span className="block h-12 w-56 animate-pulse rounded bg-muted max-md:h-8 max-md:w-40" />
              ) : (
                <b className="text-5xl font-semibold leading-none tracking-[-0.02em] tabular-nums max-md:text-[32px]">
                  {formatElapsed(clock.workedSeconds)}
                </b>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <div
              className={cn(
                'grid gap-2 max-md:gap-1.5',
                clock.canUseBreaks ? 'grid-cols-3' : 'grid-cols-2'
              )}
            >
              <ClockTile
                label={t('CLOCK_IN_TIME')}
                value={formatClockTime(attendance?.StartTimeUtc, attendance?.Timezone)}
                loading={isLoading}
              />
              {clock.canUseBreaks && (
                <ClockTile
                  label={t(phase === 'break' ? 'TOTAL_BREAK' : 'BREAK_TIME')}
                  value={clock.breakSeconds > 0 ? formatElapsed(clock.breakSeconds) : '--:--:--'}
                  loading={isLoading || busy.savingBreak}
                />
              )}
              <ClockTile
                label={t('CLOCK_OUT_TIME')}
                value={formatClockTime(attendance?.EndTimeUtc, attendance?.Timezone)}
                loading={isLoading}
              />
            </div>

            {phase === 'done' && (
              // Component Map › Day completed strip: success tones, the day's WFH/WFO badge, and
              // Clock in again when second sessions are allowed.
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-md border border-success bg-success-subtle px-3 py-2.5 text-success-subtle-foreground">
                <CheckCircle className="size-5 flex-none" />
                <b className="font-semibold">
                  {t(attendance?.IsForceClockOut ? 'DAY_CLOSED_AUTOMATICALLY' : 'DAY_COMPLETED')}
                </b>
                {attendance?.Status && (
                  <Badge variant="outline">{t(`DAY_STATUS_${attendance.Status}`)}</Badge>
                )}
                <span className="flex-1" />
                {show.clockInAgain && (
                  <Button
                    onClick={actions.clockIn}
                    loading={busy.clockingIn}
                    disabled={busy.clockingIn}
                  >
                    <LogIn />
                    {t('CLOCK_IN_AGAIN')}
                  </Button>
                )}
              </div>
            )}

            {clock.isClockStateLoading && (
              <div className="flex justify-end max-md:justify-center">
                <span className="block h-9 w-1/2 animate-pulse rounded-md bg-muted" />
              </div>
            )}

            {!clock.isClockStateLoading && phase !== 'done' && (
              <div className="flex flex-wrap justify-end gap-2 [&>*:only-child]:basis-1/2 [&>*:only-child]:flex-none max-md:[&>*:only-child]:flex-auto">
                {show.clockIn && (
                  <Button
                    onClick={actions.clockIn}
                    loading={busy.clockingIn}
                    disabled={busy.clockingIn}
                    className={ACTION_BUTTON}
                  >
                    <AlarmClock />
                    {t('CLOCK_IN')}
                  </Button>
                )}

                {show.endBreak && (
                  <Button
                    variant="outline"
                    onClick={actions.endBreak}
                    loading={busy.endingBreak}
                    disabled={busy.savingBreak || Boolean(clock.endBreakBlocker)}
                    className={ACTION_BUTTON}
                  >
                    <Coffee />
                    {t('END_BREAK')}
                  </Button>
                )}

                {show.takeBreak && (
                  <Button
                    variant="outline"
                    onClick={() => actions.startBreak('Regular')}
                    loading={busy.startingBreakType === 'Regular'}
                    disabled={busy.savingBreak}
                    className={ACTION_BUTTON}
                  >
                    <Coffee />
                    {t('TAKE_BREAK')}
                  </Button>
                )}

                {show.takeLunch && (
                  <Button
                    variant="outline"
                    onClick={() => actions.startBreak('Lunch')}
                    loading={busy.startingBreakType === 'Lunch'}
                    disabled={busy.savingBreak}
                    className={ACTION_BUTTON}
                  >
                    <UtensilsCrossed />
                    {t('TAKE_LUNCH_BREAK')}
                  </Button>
                )}

                {show.clockOut && (
                  <Button
                    variant="destructive"
                    onClick={actions.clockOut}
                    disabled={busy.clockingOut}
                    className={ACTION_BUTTON}
                  >
                    <LogOut />
                    {t('CLOCK_OUT')}
                  </Button>
                )}
              </div>
            )}

            {!clock.isClockStateLoading && clock.hints.length > 0 && (
              <div className="grid gap-1">
                {clock.hints.map((hint) => (
                  <p key={hint} className="text-[13px] text-muted-foreground">
                    {hint}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {fineSummary && (
          // Component Map › Fines footer: the year's total, then each part as a chip; on a phone the
          // total opens the parts.
          <div className="-mx-6 -mb-5 mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t px-6 py-3 max-md:-mx-4 max-md:-mb-4 max-md:px-4">
            <div className="max-md:hidden">{fineTotal}</div>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 text-left md:hidden"
              aria-expanded={isFineExpanded}
              aria-label={t(isFineExpanded ? 'SHOW_LESS' : 'SHOW_MORE')}
              onClick={() => setIsFineExpanded((expanded) => !expanded)}
            >
              <span className="min-w-0 flex-1">{fineTotal}</span>
              <ChevronDown
                className={cn(
                  'size-4 flex-none transition-transform',
                  isFineExpanded && 'rotate-180'
                )}
              />
            </button>
            <div
              className={cn(
                'ml-auto flex flex-wrap gap-2',
                'max-md:ml-0 max-md:w-full max-md:flex-col',
                !isFineExpanded && 'max-md:hidden'
              )}
            >
              <FineStat label={t('ATTENDANCE')} amount={fineSummary.attendance} />
              <FineStat label={t('NON_COMPLIANCE')} amount={fineSummary.nonCompliance} />
              <FineStat label={t('LUNCH')} amount={fineSummary.lunch} />
            </div>
          </div>
        )}
        {isFineLoading && !fineSummary && (
          <div className="-mx-6 -mb-5 mt-4 border-t px-6 py-3 max-md:-mx-4 max-md:-mb-4 max-md:px-4">
            <span className="block h-6 w-56 animate-pulse rounded bg-muted" />
          </div>
        )}
      </section>

      {clock.dialogs}
    </>
  );
};
